import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, MapPin, Mail, Building } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setStatus({ type: 'success', message: t('contact.form.success') });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: t('contact.form.error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 rounded-md bg-white border border-zinc-200 text-zinc-950 placeholder-zinc-400 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 focus:outline-none transition-colors text-sm';
  const labelClass = 'block font-manrope text-xs text-zinc-500 mb-2 uppercase tracking-wider';

  return (
    <section id="contact" className="relative py-24 md:py-32" data-testid="contact-section">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14 max-w-2xl mx-auto"
        >
          <span className="inline-block font-outfit text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">
            {t('contact.label')}
          </span>
          <h2 className="font-outfit font-medium text-3xl md:text-4xl lg:text-5xl tracking-tight text-zinc-950 mb-5">
            {t('contact.title')}{' '}
            <span className="text-gradient">{t('contact.titleHighlight')}</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-zinc-600 leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3"
          >
            <form onSubmit={handleSubmit} className="soft-card p-6 md:p-8" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelClass}>{t('contact.form.name')}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="Marie Dupont"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('contact.form.email')}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="marie@entreprise.fr"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelClass}>{t('contact.form.phone')}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="+33 6 12 34 56 78"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('contact.form.subject')}</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="Développement d'une application"
                    data-testid="contact-subject"
                  />
                </div>
              </div>

              <div className="mb-5">
                <label className={labelClass}>{t('contact.form.message')}</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className={`${inputClass} resize-none`}
                  placeholder="Décrivez votre projet en quelques mots..."
                  data-testid="contact-message"
                />
              </div>

              {status.type && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-center gap-2 p-3 rounded-md mb-4 text-sm border ${
                    status.type === 'success'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-red-50 text-red-700 border-red-200'
                  }`}
                  data-testid="contact-status"
                >
                  {status.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span className="font-manrope">{status.message}</span>
                </motion.div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-zinc-950 text-white font-medium text-sm hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('contact.form.sending')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t('contact.form.submit')}
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Legal Information */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="soft-card p-6 md:p-8 h-full" data-testid="legal-info">
              <h3 className="font-outfit font-medium text-lg mb-6 text-zinc-900">
                {t('contact.legal.title')}
              </h3>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl icon-tile flex items-center justify-center flex-shrink-0">
                    <Building className="w-4 h-4 text-sky-500" />
                  </div>
                  <div>
                    <p className="font-manrope text-xs text-zinc-500 mb-0.5">{t('contact.legal.company')}</p>
                    <p className="font-manrope font-medium text-sm text-zinc-950">Med Stud Tech</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl icon-tile flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div>
                    <p className="font-manrope text-xs text-zinc-500 mb-0.5">{t('contact.legal.address')}</p>
                    <p className="font-manrope font-medium text-sm text-zinc-950">20 rue des Balkans, 75020 Paris</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl icon-tile flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-violet-500" />
                  </div>
                  <div>
                    <p className="font-manrope text-xs text-zinc-500 mb-0.5">{t('contact.legal.siren')}</p>
                    <p className="font-manrope font-medium text-sm text-zinc-950 font-mono tracking-wide">924 618 275</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl icon-tile flex items-center justify-center flex-shrink-0">
                    <Building className="w-4 h-4 text-sky-500" />
                  </div>
                  <div>
                    <p className="font-manrope text-xs text-zinc-500 mb-0.5">{t('contact.legal.siret')}</p>
                    <p className="font-manrope font-medium text-sm text-zinc-950 font-mono tracking-wide">924 618 275 00011</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-zinc-200">
                <p className="font-manrope text-xs text-zinc-500 leading-relaxed italic">
                  « {t('footer.tagline')} »
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
